import { LanguageTextReplacementFunction, LocalizationMessages } from "../../App/Localization"
import { Block, BlockField, BlockRow } from "../../Components/Block"
import { LocalizedMessage } from "../../Components/LocalizedMessage"
import { OverlayScrollBody } from "../../Components/OverlayScrollBody"

// The two bodies of the "Close LOGBOOK" confirm overlay, split out of
// `OverlayContentView`'s dispatch so every state is reachable from props alone.
// Both are purely presentational: `AppOverlayContentView` owns the ship lookup,
// the `navigator.onLine` read and the two-step force-close state, and picks
// which body to render. That keeps the offline / close-failed / in-flight /
// force-warning states catalogable, which they are not while they depend on a
// browser global and on state private to the dispatching component.

interface MessageCallback {
  (messages: LocalizationMessages): string;
}

export const CloseShipViewsData = {
  // Which warning the confirm body leads with. Offline outranks a failed sync:
  // without internet the close cannot even be attempted, so saying so is more
  // actionable than warning about data that could not be uploaded (#541).
  message(noInternet: boolean, syncFailed: boolean): MessageCallback {
    if (noInternet) {
      return (m) => m.shipClose.internet;
    }
    if (syncFailed) {
      return (m) => m.shipClose.noSync;
    }
    return (m) => m.shipClose.text;
  },
  // Which message a FAILED close reports. Previously there was only one, and it
  // blamed the internet unconditionally -- so @almute saw "check your internet
  // connection" with the Sync menu showing the internet green, because the real cause
  // was a stale claim token (#1453). The close no longer fails for that reason at all
  // (see `closeShip`), which is exactly why the remaining causes must not inherit its
  // wording.
  //
  // Deliberately conservative: a cause is named only when it is known. A 400 from the
  // app's own close route says the close did not complete, not why, so it gets a
  // cause-neutral message rather than an invented one.
  closeFailureMessage({ noInternet, httpStatus }: { noInternet: boolean; httpStatus?: number }): string {
    if (noInternet) {
      return "ship_close_failed_offline";
    }
    if (httpStatus != undefined && httpStatus >= 500) {
      return "ship_close_failed_server";
    }
    return "ship_close_failed";
  },
  // Force close is offered only where the normal, cloud-backed close cannot get
  // through: offline up front, or after one has actually failed (#1279).
  forceCloseAvailable(noInternet: boolean, shipCloseFailed?: boolean): boolean {
    return noInternet || shipCloseFailed === true;
  },
};

interface CloseShipConfirmViewProps {
  shipName?: string;
  noInternet: boolean;
  syncFailed: boolean;
  shipClosing?: boolean;
  shipCloseFailed?: boolean;
  /// HTTP status of the failed close, when there was one. Only used to pick the
  /// message; whether force close is offered depends on `shipCloseFailed` alone.
  shipCloseFailedStatus?: number;
  onCancel: () => void;
  onCloseShip: () => void;
  onForceCloseShip: () => void;
  t: LanguageTextReplacementFunction;
}

export function CloseShipConfirmView({ shipName, noInternet, syncFailed, shipClosing, shipCloseFailed, shipCloseFailedStatus, onCancel, onCloseShip, onForceCloseShip, t }: CloseShipConfirmViewProps) {
  return (
    <OverlayScrollBody className="Overlay-body message-text-bigger Overlay-body-padding" readMoreLabel={t("read_more")}>
      <Block>
        <BlockRow>
          <LocalizedMessage html={true} message={CloseShipViewsData.message(noInternet, syncFailed)} replace={{ name: shipName || "" }} />
        </BlockRow>
        <hr />
        <BlockRow>
          <LocalizedMessage html={true} message={(m) => m.shipClose.keepOpen} />
        </BlockRow>
        {shipCloseFailed && (
          <BlockRow>
            <BlockField className="error-message">
              {t(CloseShipViewsData.closeFailureMessage({ noInternet, httpStatus: shipCloseFailedStatus }))}
            </BlockField>
          </BlockRow>
        )}
        <BlockRow actions={true} className="block-row-one-row">
          <BlockField>
            <button className="button" disabled={shipClosing} onClick={onCancel}>{t("ship_close_confirm_cancel")}</button>
          </BlockField>
          {!noInternet && (
            <BlockField>
              {/* The close is a cloud round trip: show it is running and refuse a
                  second submit meanwhile, rather than leaving an idle-looking
                  button the user taps again (#1279). */}
              <button className="button default" disabled={shipClosing} onClick={onCloseShip}>
                {shipClosing ? t("ship_close_confirm_closing") : t("ship_close_confirm")}
              </button>
            </BlockField>
          )}
          {CloseShipViewsData.forceCloseAvailable(noInternet, shipCloseFailed) && (
            <BlockField>
              <button className="button warning" disabled={shipClosing} onClick={onForceCloseShip}>
                {t("ship_force_close")}
              </button>
            </BlockField>
          )}
        </BlockRow>
      </Block>
    </OverlayScrollBody>
  );
}

interface CloseShipForceWarningViewProps {
  shipName?: string;
  shipClosing?: boolean;
  onCancel: () => void;
  onForceCloseShip: () => void;
  t: LanguageTextReplacementFunction;
}

// Second, deliberate confirmation for the local-only close: it leaves the ship
// claimed in the cloud, so the warning has to say that the claim must be
// cleared there by hand before the ship can be opened again (#1279).
export function CloseShipForceWarningView({ shipName, shipClosing, onCancel, onForceCloseShip, t }: CloseShipForceWarningViewProps) {
  return (
    <OverlayScrollBody className="Overlay-body message-text-bigger Overlay-body-padding" readMoreLabel={t("read_more")}>
      <Block>
        <BlockRow>
          <LocalizedMessage html={true} message={(m) => m.shipClose.forceWarning} replace={{ name: shipName || "" }} />
        </BlockRow>
        <BlockRow actions={true} className="block-row-one-row">
          <BlockField>
            <button className="button" disabled={shipClosing} onClick={onCancel}>{t("ship_close_confirm_cancel")}</button>
          </BlockField>
          <BlockField>
            <button className="button warning" disabled={shipClosing} onClick={onForceCloseShip}>
              {shipClosing ? t("ship_close_confirm_closing") : t("ship_force_close_confirm")}
            </button>
          </BlockField>
        </BlockRow>
      </Block>
    </OverlayScrollBody>
  );
}
